import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';

import CompetitorCard from './CompetitorCard';
import { AKA, SHIRO } from '../badges/CompetitorPositionBadge';

/**
 * Competition Design System — the SINGLE match representation: two stacked
 * CompetitorCards (upper is ALWAYS AKA, lower is ALWAYS SHIRO — the role
 * belongs to the position, enforced here so no consumer can get it wrong) +
 * a winner indicator, an optional score and an optional match-status badge.
 *
 * BracketView composes this instead of hand-rendering rows; the schedule,
 * match view, live results and scoreboard will reuse it too. Each slot
 * accepts `slotProps` (DOM attributes / handlers) so a consumer can layer on
 * interaction (drag & drop, winner clicks, data-testids) WITHOUT MatchCard
 * knowing anything about it.
 */
const useStyles = makeStyles(theme => ({
    match: {
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        borderLeft: '2px solid',
        borderLeftColor: theme.palette.text.disabled,
        background: theme.palette.background.paper,
        boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
        borderRadius: '0 3px 3px 0',
        overflow: 'hidden',
    },
    decided: {
        borderLeftColor: theme.palette.success ? theme.palette.success.main : '#4caf50',
    },
    slot: {
        flex: '1 1 0',
        minHeight: 0,
        display: 'flex',
    },
    divider: {
        flexShrink: 0,
        borderTop: '1px solid',
        borderTopColor: theme.palette.divider,
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 6px',
        borderTop: '1px solid',
        borderTopColor: theme.palette.divider,
    },
}));

/** A slot descriptor → CompetitorCard props (position stripped) + its wrapper props. */
const splitSlot = (slot = {}) => {
    const { slotProps = {}, ...competitor } = slot;
    return { competitor, slotProps };
};

/**
 * Render one slot wrapper: MatchCard owns the flex layout class, the consumer
 * layers on interaction via slotProps — so their classNames must MERGE, not
 * clobber the layout.
 */
const Slot = ({ classes, position, slotProps, children }) => {
    const { className: extra, ...rest } = slotProps;
    return (
        <div
            className={[classes.slot, extra].filter(Boolean).join(' ')}
            data-testid='match-card-slot'
            data-position={position}
            {...rest}
        >
            {children}
        </div>
    );
};

const MatchCard = props => {
    const {
        aka, shiro, decided, firstRound, dividerSpacing, size, className, style,
        containerProps, score, status,
    } = props;
    const classes = useStyles();

    const a = splitSlot(aka);
    const s = splitSlot(shiro);

    // Purely presentational walkover: in the FIRST round an empty slot facing
    // an occupied one is an auto-advance (the backend keeps its own BYE). We
    // never override an explicitly supplied status.
    const walkoverStatus = (slot, sibling) =>
        firstRound && slot.empty && !sibling.empty && !slot.status ? 'walkover' : undefined;

    const akaStatus = a.competitor.status || walkoverStatus(a.competitor, s.competitor);
    const shiroStatus = s.competitor.status || walkoverStatus(s.competitor, a.competitor);

    return (
        <div
            className={[classes.match, decided ? classes.decided : '', className].filter(Boolean).join(' ')}
            style={style}
            data-testid='match-card'
            data-decided={decided ? 'true' : 'false'}
            {...containerProps}
        >
            <Slot classes={classes} position={AKA} slotProps={a.slotProps}>
                <CompetitorCard {...a.competitor} status={akaStatus} position={AKA} size={size} />
            </Slot>
            <div className={classes.divider} style={{ height: dividerSpacing }} />
            <Slot classes={classes} position={SHIRO} slotProps={s.slotProps}>
                <CompetitorCard {...s.competitor} status={shiroStatus} position={SHIRO} size={size} />
            </Slot>
            {(score || status) && (
                <div className={classes.footer}>
                    {score && <span data-testid='match-card-score'>{score}</span>}
                    {status}
                </div>
            )}
        </div>
    );
};

MatchCard.propTypes = {
    /** Upper competitor (AKA) — CompetitorCard props + optional `slotProps`. */
    aka: PropTypes.object,
    /** Lower competitor (SHIRO) — CompetitorCard props + optional `slotProps`. */
    shiro: PropTypes.object,
    decided: PropTypes.bool,
    firstRound: PropTypes.bool,
    dividerSpacing: PropTypes.number,
    size: PropTypes.oneOf(['sm', 'md']),
    className: PropTypes.string,
    style: PropTypes.object,
    /** Spread onto the match container — lets a consumer set data-testids etc. */
    containerProps: PropTypes.object,
    score: PropTypes.node,
    status: PropTypes.node,
};

MatchCard.defaultProps = {
    aka: {},
    shiro: {},
    decided: false,
    firstRound: false,
    dividerSpacing: 1,
    size: 'md',
    className: null,
    style: null,
    containerProps: null,
    score: null,
    status: null,
};

/**
 * BracketView rebuilds `match`/`ctx`/`slotProps` as brand-new object and
 * closure literals on EVERY render (buildBracketData, makeSlotDescriptor) —
 * a default React.memo shallow-prop comparison would never hit its cache,
 * since every prop is a "new" reference even when nothing the user can see
 * actually changed. This compares the PLAIN VALUES those objects carry
 * instead (front/docs/SPA_ARCHITECTURE_AUDIT.md finding CW2 — selecting one
 * winner must not re-render every match in the bracket).
 *
 * Event handlers (onClick/onDragStart/onDragOver/onDrop) are deliberately
 * EXCLUDED from the comparison: a new closure is created every render, but
 * it always closes over the SAME logical `result` row (verified by the
 * other slotProps fields — data-participant, data-win, data-round, …
 * already changing when the row's identity/state actually does) plus the
 * caller's own `onSlotClick`/`onSlotSwap`, so skipping a re-render never
 * leaves a stale handler bound to outdated data.
 */
const shallowEqualPlainObject = (a, b, skipKeys) => {
    const objA = a || {};
    const objB = b || {};
    const keysA = Object.keys(objA).filter(k => !skipKeys || !skipKeys.includes(k));
    const keysB = Object.keys(objB).filter(k => !skipKeys || !skipKeys.includes(k));
    if (keysA.length !== keysB.length) {
        return false;
    }
    return keysA.every(k => objA[k] === objB[k]);
};

const HANDLER_KEYS = ['onClick', 'onDragStart', 'onDragOver', 'onDrop'];

const slotsEqual = (a, b) => {
    const { slotProps: slotPropsA, ...restA } = a || {};
    const { slotProps: slotPropsB, ...restB } = b || {};
    return shallowEqualPlainObject(restA, restB)
        && shallowEqualPlainObject(slotPropsA, slotPropsB, HANDLER_KEYS);
};

export const areMatchCardPropsEqual = (prev, next) =>
    prev.decided === next.decided
    && prev.firstRound === next.firstRound
    && prev.dividerSpacing === next.dividerSpacing
    && prev.size === next.size
    && prev.className === next.className
    && prev.score === next.score
    && prev.status === next.status
    && shallowEqualPlainObject(prev.style, next.style)
    && shallowEqualPlainObject(prev.containerProps, next.containerProps)
    && slotsEqual(prev.aka, next.aka)
    && slotsEqual(prev.shiro, next.shiro);

export default React.memo(MatchCard, areMatchCardPropsEqual);
